import React, { useEffect, useMemo, useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { DateTime } from 'luxon'
import {
  Btn,
  CustomCalendar,
  CustomDataTableHeader,
  CustomDropdown,
  CustomDropdownWithFilter,
  CustomModal,
  NumeroDecimal,
} from '../../../../components'
import { getNotasDisponiveisNfse } from '../../../../services/api/https/fiscal/nfse.http'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'
import { getPeriodoUltimos30Dias } from '../../../../services/util/date.service'
import { formatarMoeda } from './emitirNfse.util'
import './EmitirNfse.css'

const estiloDica = { color: 'gray', fontSize: 12 }
const TODAS_AS_CIDADES = { id: null, name: 'Todas as cidades' }

// Notas do TP já importadas e ainda sem CT-e ou NFS-e, para compor a NFS-e emitida pela 11.1
export default function ModalBuscarNotasNfse({
  visible,
  tpId,
  ufs,
  carregarCidades,
  municipioPadrao,
  empresaPadraoId,
  idsIncluidos,
  onClose,
  onAdicionar,
  onError,
}) {
  const empresas = useMemo(() => getEmpresas(), [])
  const [periodo, setPeriodo] = useState(getPeriodoUltimos30Dias)
  const [empresa, setEmpresa] = useState(null)
  const [uf, setUf] = useState(null)
  const [cidades, setCidades] = useState([TODAS_AS_CIDADES])
  const [cidade, setCidade] = useState(TODAS_AS_CIDADES)
  const [notas, setNotas] = useState([])
  const [selecionadas, setSelecionadas] = useState([])
  const [limiteAtingido, setLimiteAtingido] = useState(false)
  const [buscando, setBuscando] = useState(false)
  const [buscou, setBuscou] = useState(false)
  const [globalFilter, setGlobalFilter] = useState('')

  useEffect(() => {
    if (!visible) return
    setNotas([])
    setSelecionadas([])
    setBuscou(false)
    setLimiteAtingido(false)
    setGlobalFilter('')
    setEmpresa(
      empresas.find(item => item.id === empresaPadraoId) ||
        (empresas.length === 1 ? empresas[0] : null),
    )
    setUf(municipioPadrao ? ufs.find(item => item.uf === municipioPadrao.uf) || null : null)
    setCidade(
      municipioPadrao ? { id: municipioPadrao.id, name: municipioPadrao.name } : TODAS_AS_CIDADES,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  useEffect(() => {
    let ativo = true
    if (!uf) {
      setCidades([TODAS_AS_CIDADES])
      return undefined
    }
    carregarCidades(uf.id)
      .then(lista => ativo && setCidades([TODAS_AS_CIDADES, ...lista]))
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uf])

  // Events
  const onBtnBuscarClicked = async () => {
    if (!empresa) return onError('Selecione a Empresa')
    if (!periodo.dataInicial || !periodo.dataFinal) return onError('Informe o período')
    try {
      setBuscando(true)
      const resultado = await getNotasDisponiveisNfse(tpId, {
        pInicial: periodo.dataInicial,
        pFinal: periodo.dataFinal,
        companyId: empresa.id,
        cityId: cidade ? cidade.id : null,
      })
      setNotas(resultado.notas.filter(nota => !idsIncluidos.includes(nota.id)))
      setLimiteAtingido(resultado.limite_atingido)
      setSelecionadas([])
      setBuscou(true)
    } catch (error) {
      onError(error)
    } finally {
      setBuscando(false)
    }
  }

  const onBtnAdicionarClicked = () => {
    if (!selecionadas.length) return onError('Selecione ao menos uma nota')
    onAdicionar(selecionadas.map(nota => nota.id))
  }

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header="Adicionar notas à NFS-e"
      width={950}
      onClose={onClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <div className="p-grid">
        <div className="p-col-12">
          <span style={estiloDica}>
            Notas do Operador Logístico já importadas que ainda não têm CT-e nem NFS-e. O período é
            o da importação da nota.
          </span>
        </div>
        <div className="p-md-2 p-col-12">
          <CustomCalendar
            label="Período inicial:*"
            value={periodo.dataInicial}
            onChange={dataInicial => setPeriodo(atual => ({ ...atual, dataInicial }))}
          />
        </div>
        <div className="p-md-2 p-col-12">
          <CustomCalendar
            label="Período final:*"
            value={periodo.dataFinal}
            onChange={dataFinal => setPeriodo(atual => ({ ...atual, dataFinal }))}
          />
        </div>
        <div className="p-md-3 p-col-12">
          <CustomDropdown
            optionLabel="name"
            options={empresas}
            value={empresa}
            label="Empresa:*"
            filter={empresas.length > 5}
            onChange={setEmpresa}
          />
        </div>
        <div className="p-md-2 p-col-12 EmitirNfse-uf">
          <CustomDropdown
            optionLabel="uf"
            options={ufs}
            value={uf}
            label="UF:"
            filter={true}
            onChange={value => {
              setUf(value)
              setCidade(TODAS_AS_CIDADES)
            }}
          />
        </div>
        <div className="p-md-3 p-col-12">
          <CustomDropdownWithFilter
            optionLabel="name"
            options={cidades}
            value={cidade}
            label="Cidade do cliente:"
            filter={true}
            disabled={!uf}
            onChange={setCidade}
          />
        </div>
        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnBuscarClicked} isLoading={buscando}>
            Buscar notas
          </Btn>
        </div>

        <div className="p-col-12">
          <DataTable
            value={notas}
            dataKey="id"
            selection={selecionadas}
            onSelectionChange={event => setSelecionadas(event.value)}
            paginator={true}
            rows={10}
            globalFilter={globalFilter}
            header={<CustomDataTableHeader onChange={setGlobalFilter} />}
            emptyMessage={
              buscou
                ? 'Nenhuma nota disponível com esses filtros'
                : 'Use os filtros e busque as notas'
            }
          >
            <Column selectionMode="multiple" style={{ width: '3em' }} />
            <Column field="identifier" header="Nota" style={{ width: 110 }} />
            <Column field="client_nome" header="Cliente" />
            <Column
              field="client_city_name"
              header="Cidade"
              style={{ width: 170 }}
              body={nota => `${nota.client_city_name}/${nota.client_uf}`}
            />
            <Column
              header="Peso"
              style={{ width: 100, textAlign: 'center' }}
              body={nota => <NumeroDecimal>{nota.weightTotal}</NumeroDecimal>}
            />
            <Column
              header="Valor NF"
              style={{ width: 120, textAlign: 'center' }}
              body={nota => formatarMoeda(nota.valor_nf)}
            />
            <Column
              header="Importação"
              style={{ width: 110, textAlign: 'center' }}
              body={nota => DateTime.fromISO(nota.data_importacao).toFormat('dd/MM/yyyy')}
            />
          </DataTable>
          {limiteAtingido && (
            <span style={{ color: '#856404', fontSize: 12 }}>
              Mostrando as 500 notas mais recentes: refine o período ou a cidade para ver as demais.
            </span>
          )}
        </div>

        <div
          className="p-col-12"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span style={estiloDica}>{selecionadas.length} nota(s) selecionada(s)</span>
          <Btn onClick={onBtnAdicionarClicked} disabled={!selecionadas.length}>
            Adicionar à NFS-e
          </Btn>
        </div>
      </div>
    </CustomModal>
  )
}
